"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { toast } from "sonner";
import { bookingSchema, BookingFormData } from "@/lib/validations";
import { Booking } from "@/lib/types";

interface UseBookingFormProps {
  defaultService?: string;
  isManual?: boolean;
  onSuccess?: () => void;
  // Kept for backward compatibility with the existing "Book Follow Up" button
  // in the dashboard, which still passes a Booking to prefill from. The old
  // parentId/locked-fields behavior is gone — this just prefills the form.
  initialData?: Booking | null;
}

interface BlockedRange {
  from: string;
  to: string;
  reason?: string;
}

/**
 * useBookingForm Hook
 * Manages the state and logic for the booking form.
 */
export const useBookingForm = ({
  defaultService,
  isManual,
  onSuccess,
  initialData,
}: UseBookingFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slotCounts, setSlotCounts] = useState<Record<string, number>>({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [blockedRanges, setBlockedRanges] = useState<BlockedRange[]>([]);

  useEffect(() => {
    const fetchBlockedDates = async () => {
      try {
        const res = await fetch("/api/settings/blocked-dates");
        if (res.ok) {
          setBlockedRanges(await res.json());
        }
      } catch (error) {
        console.error("Failed to fetch blocked dates:", error);
      }
    };
    fetchBlockedDates();
  }, []);

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      name: "",
      age: "",
      gender: "",
      phone: "",
      countryCode: "+91",
      preferredDate: "",
      preferredTime: "",
      service: defaultService || "",
    },
  });

  // Prefill from an existing booking (used by the legacy "Book Follow Up"
  // entry point). Date/time always start blank for a new booking.
  useEffect(() => {
    if (initialData) {
      const name =
        initialData.name ||
        `${initialData.firstName || ""} ${initialData.lastName || ""}`.trim();

      let gender = "";
      if (initialData.gender) {
        gender =
          initialData.gender.charAt(0).toUpperCase() +
          initialData.gender.slice(1).toLowerCase();
      }

      form.reset({
        name,
        phone: initialData.phone || "",
        age: initialData.age ? String(initialData.age) : "",
        gender,
        service:
          initialData.service === "None/Not listed"
            ? ""
            : initialData.service || "",
        countryCode:
          initialData.countryCode ||
          (initialData.phone?.startsWith("+")
            ? initialData.phone.split(/\d{10}$/)[0]
            : "+91"),
        preferredDate: "",
        preferredTime: "",
      });

      setSelectedDate(undefined);
    }
  }, [initialData, form]);

  useEffect(() => {
    if (defaultService && defaultService !== "None/Not listed") {
      form.setValue("service", defaultService);
    }
  }, [defaultService, form]);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      form.setValue("preferredDate", dateStr);
      fetchSlotCounts(dateStr);
    } else {
      setSlotCounts({});
    }
  }, [selectedDate, form]);

  const fetchSlotCounts = async (date: string) => {
    setIsLoadingSlots(true);
    try {
      const response = await fetch(`/api/appointments/slots?date=${date}`);
      if (response.ok) {
        const data = await response.json();
        setSlotCounts(data.slotCounts || {});
      }
    } catch (error) {
      console.error("Failed to fetch slot counts:", error);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const onSubmit = async (data: BookingFormData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          service: data.service || "",
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          toast.error(
            "Too many requests. Please wait a few minutes and try again."
          );
        } else {
          toast.error(result.error || "Failed to submit booking");
        }
        return;
      }

      toast.success(
        isManual
          ? "Manual booking added successfully!"
          : "Booking submitted successfully! We'll contact you shortly."
      );

      form.reset();
      setSelectedDate(undefined);
      onSuccess?.();
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    slotCounts,
    isLoadingSlots,
    selectedDate,
    setSelectedDate,
    blockedRanges,
    onSubmit: form.handleSubmit(onSubmit),
  };
};
